// The spec master: every subject that can carry a specification, and the version
// in force for it right now.
//
// KEYED ON A SUBJECT, NOT A PRODUCT. A spec keyed on a product and captured during
// a run covers goods in, in process and finished product — and excludes every
// monitoring programme that has neither: environmental swabs, pest stations, glass
// and brittle plastic, chemical control, cleaning validation. So the master lists
// PRODUCT, LINE, AREA and MATERIAL subjects side by side, and this page must not quietly
// become a product list again. (ASSET and SUPPLIER have no master table in slice
// 1, so they have no rows to list yet — they are still valid subject types, and a
// draft can be created against one by id below.)
//
// ⚠ WHY THIS PAGE FANS OUT PER SUBJECT, AND WHY IT ASKS TWICE.
//
// Which version is IN FORCE is a question about a subject AT A MOMENT (`GET
// /specs?subjectType=&subjectId=&at=`), resolved against EffectiveFrom and
// EffectiveTo. A flat list of rows would have to answer it by guessing, so it is
// resolved per subject, in small batches, and a subject whose lookup fails says
// so in its own row rather than emptying the table.
//
// What EXISTS is a different question, and the second call answers it. `GET
// /spec-versions?subjectType=&subjectId=` lists every version whatever its
// status. (An earlier version of this comment said no such route existed and
// that its absence was deliberate. The absence was real; the justification was
// not. It meant a draft loaded by a migration was invisible here and unreachable
// from the detail page, so the 41 drafts migrations 004 and 007 wrote could never
// be read or signed by anyone — see the header of SpecVersion.tsx.) Resolution
// still never returns a draft. Review now can.
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { api } from '../../lib/api';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/DataTable';
import { SpecDraftEditor } from './SpecDraftEditor';
import {
  fieldClass,
  labelClass,
  localDate,
  officeFocus,
  rows,
  serverMessage,
  subjectKey,
  SUBJECT_TYPES,
  useResource,
  type SubjectType,
} from '../../shells/OfficeShell';

interface Named {
  id: number;
  code: string;
  name: string;
}

/** GET /specs — quality_spec. `inForce: false` is a real answer, not an error:
 *  no APPROVED version covers this subject at this instant, and a draft is
 *  deliberately not returned in its place. */
interface SpecPayload {
  inForce: boolean;
  message?: string | null;
  version: {
    id: number;
    version: number;
    status: 'DRAFT' | 'APPROVED' | 'SUPERSEDED';
    effectiveFrom: string;
    effectiveTo: string | null;
    approvedByUpn: string | null;
    approvedAt: string | null;
  } | null;
  parameterCount?: number;
}

interface Subject {
  type: SubjectType;
  id: number;
  label: string;
}

/** One row of `GET /spec-versions` — enough to say a draft is waiting and how big
 *  it is, without loading its parameters. */
interface VersionRow {
  id: number;
  version: number;
  status: 'DRAFT' | 'APPROVED' | 'SUPERSEDED';
  parameterCount: number;
}

interface Resolved {
  spec: SpecPayload | null;
  /** Every version the subject has, whatever its status. `null` means the lookup
   *  has not answered — never "there are none". */
  versions: VersionRow[] | null;
  error: string | null;
}

/** Concurrency for the fan-out. Enough to fill a table quickly; low enough that a
 *  master of a few hundred subjects does not open a few hundred sockets at once. */
const BATCH = 8;

function day(iso: string | null | undefined): string {
  if (!iso) return '—';
  return localDate(iso) ?? iso;
}

/** One row of the master: a subject and what its two lookups answered. */
interface MasterRow extends Subject {
  key: string;
  /** Undefined while the lookups are in flight — never "there are none". */
  answer: Resolved | undefined;
  live: NonNullable<SpecPayload['version']> | null;
  /** The newest draft, if any. */
  draft: VersionRow | undefined;
  /** The newest APPROVED version's number when the newest draft is OLDER than it (UX-02), else null.
   *  Such a draft can never be approved - the server refuses it `older_than_in_force` - so it is not
   *  waiting for anybody's signature. */
  draftOlderThan: number | null;
}

/** The newest draft of a subject, and whether it is older than the newest approved version (UI/UX
 *  audit, 31 Oct 2026, UX-02). Compared with the newest APPROVED row of `/spec-versions`, the same
 *  row the approve route compares with (the one with no EffectiveTo), never with `/specs`, which
 *  answers a different question (in force at this instant). Exported for its test. */
export function draftStanding(versions: readonly VersionRow[] | null | undefined): {
  draft: VersionRow | undefined;
  olderThan: number | null;
} {
  const list = versions ?? [];
  const draft = list.filter((v) => v.status === 'DRAFT').sort((a, b) => b.version - a.version)[0];
  const approved = list.filter((v) => v.status === 'APPROVED').reduce<number | null>((m, v) => (m === null || v.version > m ? v.version : m), null);
  return { draft, olderThan: draft && approved !== null && draft.version < approved ? approved : null };
}

/** The Live version cell's words, for search and sort. */
function liveText(r: MasterRow): string {
  if (r.answer === undefined) return 'reading…';
  if (r.answer.error) return 'not read';
  return r.live ? `v${r.live.version}` : 'none approved';
}

const parameterCount = (r: MasterRow) =>
  r.answer?.spec?.inForce ? (r.answer.spec.parameterCount ?? null) : null;

const mcol = createColumnHelper<MasterRow>();
const masterColumns = [
  mcol.accessor('label', {
    id: 'subject',
    header: 'Subject',
    cell: (c) => (
      <Link to={`/office/specs/${c.row.original.key}`} className={`underline ${officeFocus}`}>
        {c.getValue()}
      </Link>
    ),
    meta: { rowHeader: true },
  }),
  mcol.accessor('type', { header: 'Type', meta: { nowrap: true } }),
  mcol.accessor(liveText, {
    id: 'live',
    header: 'Live version',
    sortingFn: (a, b) => (a.original.live?.version ?? -1) - (b.original.live?.version ?? -1),
    cell: (c) => {
      const r = c.row.original;
      return r.answer === undefined ? (
        <span className="text-(--muted-2)">reading…</span>
      ) : r.answer.error ? (
        // The lookup failed. Not the same thing as "no version", and showing it
        // as one would say this subject cannot run when nobody has actually asked.
        <span className="font-semibold text-(--warn)">not read</span>
      ) : r.live ? (
        <span className="font-mono font-semibold">v{r.live.version}</span>
      ) : (
        <span className="font-semibold text-(--warn)">none approved</span>
      );
    },
    meta: { nowrap: true },
  }),
  // Immediately after the live version, not at the far right. The whole point of
  // the column is that a waiting draft is the next thing to DO, and a column you
  // have to scroll sideways to find is one nobody finds.
  mcol.accessor((r) => (!r.draft ? '' : r.draftOlderThan !== null ? `v${r.draft.version} · older than v${r.draftOlderThan} in force` : `v${r.draft.version} · ${r.draft.parameterCount} to read`), {
    id: 'draft',
    header: 'Draft waiting',
    sortingFn: (a, b) => (a.original.draft?.parameterCount ?? -1) - (b.original.draft?.parameterCount ?? -1),
    cell: (c) => {
      const r = c.row.original;
      return r.answer === undefined || r.answer.versions === null ? (
        <span className="text-(--muted-2)">—</span>
      ) : r.draft && r.draftOlderThan !== null ? (
        // UX-02 (UI/UX audit, 31 Oct 2026): a draft under a newer approved version is not waiting -
        // it cannot be approved at all. Named so, never as a read to do.
        <Link to={`/office/specs/${r.key}`} className={`whitespace-nowrap text-(--muted) underline ${officeFocus}`}>
          v{r.draft.version} · older than v{r.draftOlderThan} in force
        </Link>
      ) : r.draft ? (
        <Link to={`/office/specs/${r.key}`} className={`whitespace-nowrap underline ${officeFocus}`}>
          v{r.draft.version} · {r.draft.parameterCount} to read
        </Link>
      ) : (
        <span className="text-(--muted-2)">none</span>
      );
    },
  }),
  mcol.accessor((r) => r.live?.effectiveFrom ?? '', {
    id: 'from',
    header: 'Effective from',
    cell: (c) => day(c.row.original.live?.effectiveFrom),
    meta: { mono: true, nowrap: true },
  }),
  // The end of the window the live version covers. Null is "open": in force until a
  // later version is approved. Shown only for a live version: with none, there is no
  // window, and "open" would read as a claim.
  mcol.accessor((r) => (r.live ? (r.live.effectiveTo ?? 'open') : ''), {
    id: 'to',
    header: 'Effective to',
    cell: (c) => {
      const live = c.row.original.live;
      if (!live) return '—';
      return live.effectiveTo ? day(live.effectiveTo) : <span className="text-(--muted)">open</span>;
    },
    meta: { mono: true, nowrap: true },
  }),
  mcol.accessor((r) => r.live?.approvedByUpn ?? '', {
    id: 'approvedBy',
    header: 'Approved by',
    cell: (c) => c.getValue() || '—',
  }),
  mcol.accessor(parameterCount, {
    id: 'parameters',
    header: 'Parameters',
    sortingFn: (a, b) => (parameterCount(a.original) ?? -1) - (parameterCount(b.original) ?? -1),
    cell: (c) => c.getValue() ?? '—',
    meta: { align: 'right', mono: true },
  }),
];

const FIELD = fieldClass;
const LABEL = labelClass;

export function SpecMaster() {
  const navigate = useNavigate();
  const products = useResource<Named[]>('/products');
  const lines = useResource<Named[]>('/lines');
  const areas = useResource<Named[]>('/areas');
  // A raw material's spec is its supplier's PIF. Listed so a draft can be found and signed
  // from here, not only by a typed link (14 Oct 2026).
  const materials = useResource<Named[]>('/materials');

  const [resolved, setResolved] = useState<Record<string, Resolved>>({});
  const [resolving, setResolving] = useState(false);

  const [draftType, setDraftType] = useState<SubjectType>('PRODUCT');
  const [draftSubjectId, setDraftSubjectId] = useState('');
  const [draftError, setDraftError] = useState<string | null>(null);
  /** The subject the editor is open for; the type and subject fields are fixed while it is (UX-31). */
  const [writing, setWriting] = useState<{ type: SubjectType; id: number } | null>(null);
  /** UX-30: the table narrowed to the subjects with a draft waiting for a signature. */
  const [onlyWaiting, setOnlyWaiting] = useState(false);

  const namesFor = useCallback(
    (t: SubjectType): Named[] =>
      t === 'PRODUCT'
        ? rows<Named>(products.data)
        : t === 'LINE'
          ? rows<Named>(lines.data)
          : t === 'AREA'
            ? rows<Named>(areas.data)
            : t === 'MATERIAL'
              ? rows<Named>(materials.data)
              : [],
    [products.data, lines.data, areas.data, materials.data],
  );

  const subjects: Subject[] = useMemo(() => {
    const out: Subject[] = [];
    for (const t of ['PRODUCT', 'LINE', 'AREA', 'MATERIAL'] as const) {
      for (const n of namesFor(t)) out.push({ type: t, id: n.id, label: `${n.name} (${n.code})` });
    }
    return out;
  }, [namesFor]);

  // Resolve each subject's live version. Keyed by subject so a re-render does not
  // re-ask, and cancelled on unmount so a slow batch cannot write into a page the
  // user has already left.
  useEffect(() => {
    if (subjects.length === 0) return;
    let cancelled = false;
    setResolving(true);

    void (async () => {
      for (let i = 0; i < subjects.length && !cancelled; i += BATCH) {
        const batch = subjects.slice(i, i + BATCH);
        const answers = await Promise.all(
          batch.map(async (s): Promise<[string, Resolved]> => {
            const key = subjectKey(s.type, s.id);
            const q = `subjectType=${s.type}&subjectId=${s.id}`;
            try {
              // TWO QUESTIONS, TWO CALLS, AND THEY ARE NOT THE SAME QUESTION.
              // `/specs` resolves which version is IN FORCE — point-in-time,
              // approved only, the server's rule and never re-derived here.
              // `/spec-versions` lists what EXISTS, which is the only way a draft
              // loaded by a migration is visible to anyone at all.
              const [spec, versions] = await Promise.all([
                api.get(`/specs?${q}`) as Promise<SpecPayload>,
                api.get(`/spec-versions?${q}`) as Promise<VersionRow[]>,
              ]);
              return [key, { spec, versions: rows<VersionRow>(versions), error: null }];
            } catch (err: unknown) {
              // Per subject, never page-wide. One subject the API could not answer
              // for must not blank out the other forty.
              return [key, { spec: null, versions: null, error: serverMessage(err) }];
            }
          }),
        );
        if (cancelled) return;
        setResolved((prev) => ({ ...prev, ...Object.fromEntries(answers) }));
      }
      if (!cancelled) setResolving(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [subjects]);

  // Counted off ANSWERS ONLY. A subject still being read, or one whose lookup
  // failed, is not "has no approved spec" — and counting it as one would raise a
  // banner about a state nobody has established.
  //
  // ⚠⚠ PRODUCT SUBJECTS ONLY, BECAUSE THE SENTENCE IS ONLY TRUE OF THEM.
  // It used to count LINE and AREA too, and then said "No run can start against
  // them." For a LINE that is FALSE BY DESIGN: runs.ts documents that a line with
  // no approved start-up form produces no readiness obligations and blocks
  // nothing, precisely so production is not stopped to enforce paperwork that
  // does not exist. For an AREA a run is not even a concept. So the headline
  // number on this page counted 45 subjects and told the reader their line could
  // not run, on the morning a trial was deliberately configured that way.
  const withoutLiveVersion = subjects.filter(
    (s) => s.type === 'PRODUCT' && resolved[subjectKey(s.type, s.id)]?.spec?.inForce === false,
  ).length;

  /** LINE subjects with no approved start-up form. A real state worth naming, and
   *  a different sentence: these lines RUN, they are simply never held for a
   *  start-up check. Counted separately so neither number has to lie. */
  const linesWithoutForm = subjects.filter(
    (s) => s.type === 'LINE' && resolved[subjectKey(s.type, s.id)]?.spec?.inForce === false,
  ).length;

  /** Subjects holding a DRAFT that could be signed. Counted off answers only, for the same
   *  reason as above: a subject still being read is not one with no draft.
   *  ⚠ A draft OLDER than the newest approved version is not counted (UI/UX audit, 31 Oct 2026,
   *  UX-02): the banner said "3 drafts are written and waiting for a signature" when all three
   *  (MATERIAL-15, Red Quinoa, YSP Grits) were v1 drafts under an approved v2 - drafts nobody may
   *  approve, because approving one would put back the limits v2 corrected. */
  const waitingSubjects = subjects.filter((s) => {
    const { draft, olderThan } = draftStanding(resolved[subjectKey(s.type, s.id)]?.versions);
    return draft !== undefined && olderThan === null;
  });
  const awaitingSignature = waitingSubjects.length;
  /** UX-30 (UI/UX audit, 31 Oct 2026): which kinds of subject the waiting drafts are for. The banner's
   *  advice is about PRODUCT and LINE specs, and was printed over three MATERIAL drafts it said nothing
   *  about - so each sentence is said only when a draft it is about is among them. */
  const waitingProduct = waitingSubjects.some((s) => s.type === 'PRODUCT');
  const waitingLine = waitingSubjects.some((s) => s.type === 'LINE');

  const master = useMemo<MasterRow[]>(
    () =>
      subjects.map((s) => {
        const key = subjectKey(s.type, s.id);
        const answer = resolved[key];
        return {
          ...s,
          key,
          answer,
          live: answer?.spec?.inForce ? answer.spec.version : null,
          // The newest draft, if any. Shown rather than counted: what matters
          // is that there is something to read and how long the read is.
          ...(({ draft, olderThan }) => ({ draft, draftOlderThan: olderThan }))(draftStanding(answer?.versions)),
        };
      }),
    [subjects, resolved],
  );
  const shownRows = useMemo(
    () => (onlyWaiting ? master.filter((r) => r.draft !== undefined && r.draftOlderThan === null) : master),
    [master, onlyWaiting],
  );


  /** Opens the editor for the chosen subject (UX-31). Nothing is posted until the editor's Save. */
  function startDraft(e: FormEvent) {
    e.preventDefault();
    const id = Number(draftSubjectId);
    if (!Number.isInteger(id) || id <= 0) {
      setDraftError('Choose a subject.');
      return;
    }
    setDraftError(null);
    setWriting({ type: draftType, id });
  }

  const masterError = products.error ?? lines.error ?? areas.error ?? materials.error;

  return (
    <div className="flex flex-col gap-(--s5)">
      <header>
        <h1 className="m-0 font-display text-[34px] font-semibold leading-none tracking-[-0.01em]">Spec master</h1>
        <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--muted)">
          One approved version per subject. A draft takes over only once it is approved.
        </p>
      </header>

      {masterError ? (
        <p role="alert" className="m-0 text-(length:--fs-md) font-semibold text-(--warn)">
          {masterError}
        </p>
      ) : null}

      {/* Named, not left to be counted off the table. A PRODUCT with no approved
          version cannot start a run at all, and that is the state worth seeing
          before somebody discovers it at a changeover. */}
      {/* Both counts below are held while the lookups run (issue #156, 31 Oct 2026), as the drafts
          banner is (UX-10): mid-fan-out they climbed a subject at a time, a partial count read as
          a fact. The drafts banner's "Counting…" line says the page is still reading. */}
      {!resolving && withoutLiveVersion > 0 ? (
        <p className="m-0 rounded-(--radius) border border-(--warn-line) bg-(--warn-bg) p-(--s3) text-(length:--fs-sm) font-semibold text-(--warn)">
          {withoutLiveVersion} {withoutLiveVersion === 1 ? 'product has' : 'products have'} no
          approved specification. No run can start against {withoutLiveVersion === 1 ? 'it' : 'them'}.
        </p>
      ) : null}

      {/* A DIFFERENT STATE AND A DIFFERENT SENTENCE. A line with no approved
          start-up form still runs — runs.ts refuses to stop production over
          paperwork that does not exist. What it loses is the hold: nothing is
          ever asked before the line starts, which looks identical to a line that
          passed. Worth saying plainly, and never as "no run can start". */}
      {!resolving && linesWithoutForm > 0 ? (
        <p className="m-0 rounded-(--radius) border border-(--border-strong) bg-(--subtle) p-(--s3) text-(length:--fs-sm)">
          {linesWithoutForm} {linesWithoutForm === 1 ? 'line has' : 'lines have'} no approved
          start-up form. Runs still start on {linesWithoutForm === 1 ? 'it' : 'them'} and product
          checks are never held — nothing is asked before the line runs, which is not the same as a
          line that passed.
        </p>
      ) : null}

      {/* The actionable half of the banner above. "Nothing is approved" is a
          problem statement; "31 drafts are written and waiting for a signature"
          is the next thing to do, and until 2026-09-19 there was no screen that
          could say it — a migration-loaded draft was invisible and unreachable. */}
      {/* ⚠ HELD WHILE THE LOOKUPS RUN (UI/UX audit, 31 Oct 2026, UX-10; rule 15). The count is
          off answers only, so mid-fan-out it climbed "1 draft" -> "3 drafts" over ~188 lookups:
          every figure before the last batch was a partial count read as a fact. Said as counting
          until every subject has answered. */}
      {resolving ? (
        <p aria-live="polite" className="m-0 rounded-(--radius) border border-(--border) bg-(--subtle) p-(--s3) text-(length:--fs-sm) text-(--muted)">
          Counting drafts waiting for a signature…
        </p>
      ) : awaitingSignature > 0 ? (
        <p className="m-0 rounded-(--radius) border border-(--border-strong) bg-(--subtle) p-(--s3) text-(length:--fs-sm)">
          <strong>
            {awaitingSignature} {awaitingSignature === 1 ? 'draft is' : 'drafts are'} written and
            waiting for a signature.
          </strong>{' '}
          {/* UX-30: the count reaches them - they were scattered among ~80 rows with no filter. */}
          <Button
            variant="outline"
            size="sm"
            type="button"
            aria-pressed={onlyWaiting}
            onClick={() => setOnlyWaiting((v) => !v)}
            className="mx-(--s1)"
          >
            {onlyWaiting ? 'Show every subject' : `Show only ${awaitingSignature === 1 ? 'that subject' : `those ${awaitingSignature}`}`}
          </Button>{' '}
          Open a subject to read the draft and approve it.{' '}
          {/* ⚠ NO LONGER A FIXED INSTRUCTION TO "START WITH A LINE". Approving a
              line spec creates readiness obligations that can ONLY reach MET
              through a Verification, so on a site where nobody holds VERIFY it
              silently makes every line unreleasable. This page cannot know which
              order is right; it can say what each choice costs. */}
          {waitingProduct ? 'A PRODUCT spec is what a run needs to start at all. ' : ''}
          {waitingLine
            ? 'A LINE spec adds the start-up form, which holds every run on that line until a second ' +
              'person signs it off — so approve one only when somebody holds VERIFY.'
            : ''}
        </p>
      ) : null}

      <DataTable
        data={shownRows}
        columns={masterColumns}
        caption="Subjects and the specification version in force"
        getRowId={(r) => r.key}
        rowHref={(r) => `/office/specs/${r.key}`}
        search="Search subject, type or approver"
        loading={products.loading}
        error={masterError}
        onRetry={() => {
          products.reload();
          lines.reload();
          areas.reload();
          materials.reload();
        }}
        empty="No products, lines or areas are registered, so there is nothing to specify yet."
      />

      <p aria-live="polite" className="m-0 text-(length:--fs-xs) text-(--muted)">
        {resolving
          ? `Resolving the version in force for ${subjects.length} subjects…`
          : `${subjects.length} subjects.`}
      </p>

      <section className="rounded-(--radius) border border-(--border) bg-(--card) shadow-card p-(--s4)">
        <h2 className="m-0 font-display text-[22px] font-semibold leading-tight">New draft</h2>
        <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--muted)">
          A draft is editable and judges nothing — readings are only ever taken against an approved
          version. To add a version to a subject that already has one, open the subject and draft it
          from the version in force, so the parameters carry over.
        </p>

        {/* UX-31 (UI/UX audit, 31 Oct 2026): choose the subject, then the SAME editor a version is
            drafted from opens with one blank row. The one-parameter form it replaces had its own field
            order, labelled FIRST_OFF "at start-up" (the operator's word for READINESS), and had no
            yes/no answer or start-up kind, so a LINE draft could only ever be a measurement. */}
        <form onSubmit={startDraft} className="mt-(--s3) flex flex-col gap-(--s3)">
          <div className="flex min-w-0 flex-wrap items-end gap-(--s3)">
            <div className="flex flex-col gap-(--s1)">
              <label htmlFor="draft-type" className={LABEL}>Subject type</label>
            <select
              id="draft-type"
              value={draftType}
              disabled={writing !== null}
              onChange={(e) => {
                setDraftType(e.target.value as SubjectType);
                setDraftSubjectId('');
              }}
              className={`${FIELD} ${officeFocus}`}
            >
              {/* A MATERIAL is drafted by quality_material_spec_draft, which creates the
                  material too, with the terms its version signs (shelf life, CofA required). */}
              {SUBJECT_TYPES.filter((t) => t !== 'MATERIAL').map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

            {/* UX-32: min-w-0 and a capped full width - at iPad width a min-w-[240px] select of long
                product names pushed the page sideways (scrollWidth 1059 vs 1010). */}
            <div className="flex min-w-0 max-w-full flex-col gap-(--s1)">
              <label htmlFor="draft-subject" className={LABEL}>Subject</label>
              {namesFor(draftType).length > 0 ? (
                <select
                  id="draft-subject"
                  value={draftSubjectId}
                  disabled={writing !== null}
                  onChange={(e) => setDraftSubjectId(e.target.value)}
                  className={`${FIELD} w-full max-w-[28rem] ${officeFocus}`}
                >
                  <option value="">Choose…</option>
                  {namesFor(draftType).map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.name} ({n.code})
                    </option>
                  ))}
                </select>
              ) : (
                // ASSET and SUPPLIER have no master table in slice 1, so the id is
                // typed. Better an honest number field than a select with nothing in it.
                <input
                  id="draft-subject"
                  type="number"
                  min={1}
                  value={draftSubjectId}
                  disabled={writing !== null}
                  onChange={(e) => setDraftSubjectId(e.target.value)}
                  placeholder={`${draftType} id`}
                  className={`${FIELD} w-full max-w-[28rem] ${officeFocus}`}
                />
              )}
            </div>

            {writing === null ? (
              <Button variant="default" type="submit">
                Write the draft
              </Button>
            ) : null}
          </div>
        </form>

        {writing !== null ? (
          <div className="mt-(--s3)">
            <SpecDraftEditor
              key={subjectKey(writing.type, writing.id)}
              subjectType={writing.type}
              subjectId={writing.id}
              fromVersion={null}
              initial={[]}
              isProduct={writing.type === 'PRODUCT'}
              onSaved={(id) =>
                // Straight to the subject's page with the new draft in the address (UX-27), so a
                // reload still opens it; the state is kept for anything reading it the old way.
                navigate(`/office/specs/${subjectKey(writing.type, writing.id)}?version=${id}`, {
                  state: { draft: { id } },
                })
              }
              onCancel={() => setWriting(null)}
            />
          </div>
        ) : null}

        {draftError ? (
          <p role="alert" className="m-0 mt-(--s3) text-(length:--fs-sm) font-semibold text-(--warn)">
            {draftError}
          </p>
        ) : null}
      </section>
    </div>
  );
}

export default SpecMaster;
